import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { borderRadii, scrollbarCss } from 'app/spacing';
import { hasInputControl, MaybeInputControl } from '../InputControl';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import Validation, { useValidation } from '../Validation';
import { applyRef } from '../props';
import { getMaskedValue } from '../helpers';
import { markdownPreviewStyles } from 'components/WYSIWYG/MarkdownEditor/styles';
import { OutlineLayout } from './Outline';
import { MarkdownOverlay } from 'components/WYSIWYG/MarkdownEditor/components/MarkdownOverlay';
import { MarkdownEditor } from 'components/WYSIWYG/MarkdownEditor';
import {
  getHTMLValue,
  getRawTextValue,
} from 'components/WYSIWYG/MarkdownEditor/helpers';
import css from '@emotion/css';
import { MergeColumn } from './Outline';
import { useTooltip } from '__components/Kizen/Tooltip';
import {
  INITIAL_HEIGHT,
  MAX_WIDTH,
} from 'components/WYSIWYG/MarkdownEditor/constants';

const OutlineTextarea = styled.div`
  width: 100%;
  overflow-y: auto;
  ${scrollbarCss}
  height: 100%;
  ${({ disabled, fullHeight, initialHeight }) =>
    disabled && fullHeight
      ? css`
          min-height: ${initialHeight}px;
          height: 100%;
        `
      : css``}
  /* Account for baseline to get accurate spacing */
  /* Account for right scrollbar width */
  padding-right: 6px;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 10px;
  & * {
    font-size: 14px;
  }

  ${markdownPreviewStyles}
`;

const StyledResizeWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${borderRadii.small};
  max-width: 100%;
  overflow: hidden;

  ${({ enableResize, minHeight, minWidth, fullHeight, disabled, error }) => css`
    height: ${fullHeight ? '100%' : `${minHeight}px`};
    min-height: ${minHeight}px;
    min-width: ${minWidth}px;
    resize: ${enableResize ? 'both' : 'none'};
    background-color: ${disabled
      ? 'var(--color-background-light)'
      : 'transparent'};
    border: 1px solid
      ${error
        ? 'var(--color-button-warning-hover)'
        : 'var(--color-border-medium)'};
  `}

  input:focus + &&& {
    border-color: var(--color-button-secondary-hover);
  }
`;

const StyledTarget = styled.div``;

const StyledInput = styled.input`
  // hides input from view
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  z-index: -1;
  pointer-events: none;
`;

export const OutlineMarkdownLongText = forwardRef((props, ref) => {
  const {
    fieldId,
    className,
    value = '',
    placeholder = null,
    disabled = null,
    error = null,
    onChange = null,
    onFocus = null,
    onBlur = null,
    enableResize = false,
    setMenuOpen,
    setShown,
    handleSubmit,
    fullHeight,
    displayMaskedValue,
    initialHeight = 63,
    minWidth = 230,
    tooltipLabel,
    tooltipMaxWdith,
    isMasked = false,
    inModal = true,
    ...others
  } = props;

  const displayValue =
    displayMaskedValue &&
    isMasked &&
    value &&
    `<p>${getMaskedValue(value)}</p>`;

  const { assignFieldHandle } = useKeyBoardContext();

  const sizeRef = useRef();
  const validationRef = useRef();
  const mergeRef = useCallback(
    (el) => {
      applyRef(validationRef, el);
      applyRef(ref, el);
    },
    [ref]
  );
  const [validation, validationProps] = useValidation(validationRef, props);

  const targetRef = useRef(null);
  const editorRef = useRef(null);
  const inputRef = useRef(null);

  const {
    'data-field-type': _1,
    'data-field-id': _2,
    ...forInputControl
  } = props;

  const [open, setOpen] = useState(null);
  const [html, setHtml] = useState('');

  const handleOpen = () => {
    if (!disabled || html.trim()) {
      setOpen({
        width: Math.max(sizeRef.current?.offsetWidth, MAX_WIDTH),
        height: enableResize
          ? Math.max(sizeRef.current?.offsetHeight, INITIAL_HEIGHT)
          : undefined,
      });
    }
  };

  const handleHide = async (away = false) => {
    const editor = editorRef.current?.editor;
    if (editor && !disabled) {
      const value = await getRawTextValue(editor, true);
      if (onChange) {
        onChange(value.trim());
      }
    }
    setOpen(null);
    if (!away) {
      inputRef.current?.focus();
    }
  };

  const onHideAway = () => {
    handleHide(true);
  };

  const onHide = () => {
    handleHide(false);
  };

  const handleSave = (value) => {
    if (!disabled) {
      if (onChange) {
        onChange(value.trim());
      }
    }
    setOpen(null);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const convertAction = async () => {
      const html = await getHTMLValue(value, true, {
        condensed: true,
      });
      setHtml(html);
    };
    convertAction();
  }, [value]);

  const editRef = useRef(false);

  const handleFocus = (...args) => {
    onFocus?.(...args);
    editRef.current = true;
  };

  const handleBlur = (...args) => {
    onBlur?.(...args);
    editRef.current = false;
  };

  const openIfEdit = () => {
    if (editRef.current) {
      handleOpen();
    }
  };

  assignFieldHandle(fieldId, {
    customTab: () => {
      onHideAway();
    },
    customFocus: () => {
      handleOpen();
      inputRef.current?.focus();
      return validationRef.current;
    },
    customEnter: (e) => {
      if (!open) {
        return false;
      } else {
        const isSubmit = e.ctrlKey || e.metaKey;
        if (isSubmit) {
          onHideAway();
        }
        return !isSubmit;
      }
    },
    customUp: openIfEdit,
    customDown: openIfEdit,
    customSpace: openIfEdit,
    customInput: openIfEdit,
    customEscape: () => {
      if (open) {
        onHide();
      }
    },
    disabled,
  });

  const [tooltipProps, tooltip] = useTooltip(
    {
      html,
      maxWidth: 500,
    },
    () => !!displayValue && !open
  );

  return (
    <MaybeInputControl variant="outline" forInput {...forInputControl}>
      <StyledTarget ref={targetRef} />
      {tooltip}
      <OutlineLayout
        ref={mergeRef}
        className={!hasInputControl(props) && className}
        disabled={disabled}
        error={error || validation.error}
        fullHeight={fullHeight}
        overrideDisabledColor={disabled}
        onClick={handleOpen}
      >
        <MergeColumn fullHeight={fullHeight} maxWidth={'100%'}>
          <StyledInput
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={inputRef}
          />
          <StyledResizeWrapper
            ref={sizeRef}
            disabled={disabled}
            fullHeight={fullHeight}
            enableResize={enableResize}
            minHeight={initialHeight}
            minWidth={minWidth}
            error={!!error}
          >
            <OutlineTextarea
              dangerouslySetInnerHTML={{
                __html: disabled
                  ? displayValue || html || '—'
                  : displayValue ||
                    html ||
                    (placeholder && `<p>${placeholder}</p>`) ||
                    '',
              }}
              color={
                !displayValue && !html && placeholder
                  ? 'var(--color-font-placeholder)'
                  : 'var(--color-font-primary)'
              }
              disabled
              ref={ref}
              initialHeight={initialHeight}
              {...others}
              {...tooltipProps}
            />
          </StyledResizeWrapper>
        </MergeColumn>
      </OutlineLayout>
      <Validation {...validationProps} />
      <MarkdownOverlay
        show={!!open}
        onHide={onHideAway}
        targetRef={targetRef}
        containerRef={mergeRef}
        inModal={inModal}
        initialWidth={open?.width}
      >
        <MarkdownEditor
          isMarkdown
          initialValue={value}
          autofocus="end"
          onSave={handleSave}
          editorRef={editorRef}
          readonly={disabled}
          onBlur={(ev) => {
            validation.onBlur?.(ev);
          }}
          inModal={inModal}
          initialHeight={open?.height}
          initialWidth={open?.width}
        />
      </MarkdownOverlay>
    </MaybeInputControl>
  );
});
